import type { Order } from '@/shared/api/generated/api';

import { REVIEW_MAX_WINDOW_MS, REVIEW_MIN_DELAY_MS } from './constants';

import type { PendingReviewOrder } from '../model/types';

const toDate = (value: string | Date | undefined): Date | null => {
  if (!value) {
    return null;
  }

  const parsed = value instanceof Date ? value : new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDatePart = (value: string | Date): string => {
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  return value;
};

const parseEventStart = (order: Order): Date | null => {
  const awareStart = order.event?.aware_start_dt;

  if (awareStart) {
    return toDate(awareStart);
  }

  const date = order.event?.date;
  const time = order.event?.time;

  if (!date) {
    return null;
  }

  const timePart = time ? `T${time}` : 'T00:00:00';
  const parsed = new Date(`${formatDatePart(date)}${timePart}`);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDurationHours = (order: Order): number => {
  const eventDuration = order.event?.duration;
  const experienceDuration = order.experience?.duration;

  if (typeof eventDuration === 'number' && eventDuration > 0) {
    return eventDuration;
  }

  if (typeof experienceDuration === 'number' && experienceDuration > 0) {
    return experienceDuration;
  }

  return 0;
};

export const getOrderEventEndAt = (order: Order): Date | null => {
  const start = parseEventStart(order);

  if (!start) {
    return null;
  }

  const durationHours = getDurationHours(order);
  const endMs = start.getTime() + durationHours * 60 * 60 * 1000;

  return new Date(endMs);
};

export const isWithinReviewCollectionWindow = (eventEndAt: Date, now = new Date()): boolean => {
  const elapsed = now.getTime() - eventEndAt.getTime();

  return elapsed >= REVIEW_MIN_DELAY_MS && elapsed <= REVIEW_MAX_WINDOW_MS;
};

/**
 * Возвращает заказ, обогащённый `eventEndAt`, если он подходит для сбора отзыва,
 * иначе `null`. `getOrderEventEndAt` считается ровно один раз.
 */
export const toEligiblePendingReviewOrder = (order: Order, now = new Date()): PendingReviewOrder | null => {
  // Собираем отзыв, пока заказ доступен для отзыва и путешественник его ещё не отправил.
  const isReviewSubmitted = Boolean(order.traveler_review?.submitted);

  if (!order.is_available_for_review_by_traveler || isReviewSubmitted || !order.review_url) {
    return null;
  }

  const eventEndAt = getOrderEventEndAt(order);

  if (!eventEndAt || !isWithinReviewCollectionWindow(eventEndAt, now)) {
    return null;
  }

  return { ...order, eventEndAt };
};
